(() => {
  const elementName = 'nosht-campaign-attribution';

  if (customElements.get(elementName)) return;

  class NoshtCampaignAttribution extends HTMLElement {
    constructor() {
      super();
      this.requestInProgress = false;
      this.connected = false;
      this.storageKey = 'nosht-campaign-attribution';
      this.handleConsentChange = this.handleConsentChange.bind(this);
    }

    connectedCallback() {
      if (window.Shopify?.designMode) return;

      this.connected = true;
      document.addEventListener('visitorConsentCollected', this.handleConsentChange);
      document.addEventListener('trackingConsentAccepted', this.handleConsentChange);
      this.ensurePrivacyApi();
    }

    disconnectedCallback() {
      this.connected = false;
      document.removeEventListener('visitorConsentCollected', this.handleConsentChange);
      document.removeEventListener('trackingConsentAccepted', this.handleConsentChange);
    }

    sanitizeToken(value, maximumLength = 80) {
      if (typeof value !== 'string') return '';

      return value
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9._-]+/g, '-')
        .replace(/^[-._]+|[-._]+$/g, '')
        .slice(0, maximumLength);
    }

    getPrivacyApi() {
      return window.Shopify?.customerPrivacy || window.Shopify?.trackingConsent || null;
    }

    canStoreAttribution() {
      const privacy = this.getPrivacyApi();
      if (!privacy) return false;

      if (
        typeof privacy.analyticsProcessingAllowed === 'function' &&
        typeof privacy.marketingAllowed === 'function'
      ) {
        return privacy.analyticsProcessingAllowed() === true && privacy.marketingAllowed() === true;
      }

      if (typeof privacy.userCanBeTracked === 'function') {
        return privacy.userCanBeTracked() === true;
      }

      return false;
    }

    getAttribution() {
      const params = new URLSearchParams(window.location.search);
      const configuredReference = this.sanitizeToken(this.dataset.campaignRef || '', 64);
      const queryReference = this.sanitizeToken(params.get('ref') || '', 64);
      const pageHandle = this.sanitizeToken(this.dataset.pageHandle || '', 64);
      const campaign =
        this.sanitizeToken(params.get('utm_campaign') || '', 80) || configuredReference || pageHandle;

      return {
        campaign,
        reference: configuredReference || queryReference,
      };
    }

    readStoredSignature() {
      try {
        return window.sessionStorage.getItem(this.storageKey) || '';
      } catch (_error) {
        return '';
      }
    }

    writeStoredSignature(signature) {
      try {
        if (signature) {
          window.sessionStorage.setItem(this.storageKey, signature);
        } else {
          window.sessionStorage.removeItem(this.storageKey);
        }
      } catch (_error) {
        // Session storage may be unavailable in strict privacy modes.
      }
    }

    getCartUpdateUrl() {
      if (window.routes?.cart_update_url) return window.routes.cart_update_url;

      const root = window.Shopify?.routes?.root || '/';
      return `${root}cart/update.js`;
    }

    async updateCartAttributes(attributes, signature = '') {
      if (this.requestInProgress) return;

      this.requestInProgress = true;

      try {
        const response = await fetch(this.getCartUpdateUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ attributes }),
        });

        if (response.ok) this.writeStoredSignature(signature);
      } catch (_error) {
        // Attribution must never block the campaign page or its calls to action.
      } finally {
        this.requestInProgress = false;
      }
    }

    storeAttribution() {
      if (!this.connected || !this.canStoreAttribution()) return;

      const attribution = this.getAttribution();
      if (!attribution.campaign && !attribution.reference) return;

      const signature = JSON.stringify(attribution);
      if (this.readStoredSignature() === signature) return;

      this.updateCartAttributes(
        {
          __nosht_campaign: attribution.campaign,
          __nosht_ref: attribution.reference,
        },
        signature
      );
    }

    clearStoredAttribution() {
      if (!this.readStoredSignature()) return;

      this.updateCartAttributes(
        {
          __nosht_campaign: '',
          __nosht_ref: '',
        },
        ''
      );
    }

    handleConsentChange() {
      if (this.canStoreAttribution()) {
        this.storeAttribution();
      } else {
        this.clearStoredAttribution();
      }
    }

    ensurePrivacyApi() {
      if (this.getPrivacyApi()) {
        this.handleConsentChange();
        return;
      }

      if (typeof window.Shopify?.loadFeatures !== 'function') return;

      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (!error && this.connected) this.handleConsentChange();
        }
      );
    }
  }

  customElements.define(elementName, NoshtCampaignAttribution);
})();
